import React, { useState } from "react";
import { Form, ProgressBar } from "react-bootstrap";
import { useLanguage } from "../i18n/LanguageContext";

export interface PasswordCheck {
  length: boolean;
  uppercase: boolean;
  lowercase: boolean;
  digit: boolean;
  special: boolean;
}

export function checkPassword(password: string): PasswordCheck {
  return {
    length: password.length >= 6,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /[0-9]/.test(password),
    special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password),
  };
}

export function isPasswordValid(password: string): boolean {
  const c = checkPassword(password);
  if (!c.length) return false;
  const complexity = [c.uppercase, c.lowercase, c.digit, c.special].filter(
    Boolean,
  ).length;
  return complexity >= 2;
}

function getScore(checks: PasswordCheck): number {
  return Object.values(checks).filter(Boolean).length;
}

function getColor(score: number): string {
  if (score <= 1) return "#dc3545";
  if (score <= 2) return "#fd7e14";
  if (score <= 3) return "#0dcaf0";
  return "#198754";
}

// getLabel is now handled via t() inside the component

const EyeOpen = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const CheckIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const CircleIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="12" cy="12" r="9" />
  </svg>
);

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  showStrength?: boolean;
  controlId?: string;
  required?: boolean;
  isInvalid?: boolean;
  isValid?: boolean;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  onChange,
  label,
  placeholder,
  disabled = false,
  showStrength = false,
  controlId = "password",
  required = false,
  isInvalid,
  isValid,
}) => {
  const { t } = useLanguage();
  const [show, setShow] = useState(false);

  const checks = checkPassword(value);
  const score = getScore(checks);
  const showMeter = showStrength && value.length > 0;
  const color = getColor(score);

  const CHECK_LABELS: { key: keyof PasswordCheck; labelKey: string }[] = [
    { key: "length", labelKey: "passwordCheckLength" },
    { key: "uppercase", labelKey: "passwordCheckUppercase" },
    { key: "lowercase", labelKey: "passwordCheckLowercase" },
    { key: "digit", labelKey: "passwordCheckDigit" },
    { key: "special", labelKey: "passwordCheckSpecial" },
  ];

  const STRENGTH_LABELS: Record<number, string> = {
    0: t("passwordStrengthWeak"),
    1: t("passwordStrengthWeak"),
    2: t("passwordStrengthFair"),
    3: t("passwordStrengthGood"),
    4: t("passwordStrengthStrong"),
    5: t("passwordStrengthStrong"),
  };

  return (
    <Form.Group className="mb-3" controlId={controlId}>
      {label && <Form.Label className="small fw-semibold">{label}</Form.Label>}

      <div style={{ position: "relative" }}>
        <Form.Control
          type={show ? "text" : "password"}
          placeholder={placeholder || t("password")}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={disabled}
          isInvalid={isInvalid}
          isValid={isValid}
          style={{
            padding: "12px 44px 12px 12px",
            borderRadius: "8px",
          }}
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          disabled={disabled}
          tabIndex={-1}
          title={show ? "Hide password" : "Show password"}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 42,
            border: "none",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#6c757d",
            borderRadius: "0 8px 8px 0",
            padding: 0,
          }}
        >
          {show ? <EyeClosed /> : <EyeOpen />}
        </button>
        {isInvalid && (
          <Form.Control.Feedback type="invalid">
            {t("passwordsDoNotMatch")}
          </Form.Control.Feedback>
        )}
      </div>

      {showMeter && (
        <div className="mt-2">
          <div style={{ display: "flex", gap: 4, marginBottom: 6 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  background: i <= score ? color : "#e9ecef",
                  transition: "background 0.2s",
                }}
              />
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 6,
            }}
          >
            <small style={{ fontSize: 11, fontWeight: 600, color }}>
              {STRENGTH_LABELS[score]}
            </small>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
            {CHECK_LABELS.map(({ key, labelKey }) => (
              <small
                key={key}
                style={{
                  fontSize: 11,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  color: checks[key] ? "#198754" : "#adb5bd",
                }}
              >
                {checks[key] ? <CheckIcon /> : <CircleIcon />}
                {t(labelKey as any)}
              </small>
            ))}
          </div>
        </div>
      )}
    </Form.Group>
  );
};

export default PasswordInput;
